import styled from '@emotion/styled';
import { SearchProvider } from '@hotjar/webapp/components/SearchProvider/SearchProvider';
import { supportsMultipleSku } from '@hotjar/webapp/domains/common/product/features';
import type { Organization } from '@hotjar//organization';
import { mapSiteDtoToModel } from '@hotjar/site';
import type { Plan } from '@hotjar/webapp/domains/revenue/plan/types';
import type { PriceInfo } from '@hotjar/webapp/domains/revenue/pricing/types';
import type { SiteDTO } from '@hotjar/site';
import type { SiteWithPriceInfo } from '@hotjar/webapp/domains/revenue/site/types';
import { useOwnedAccount } from '@hotjar/account';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import { useOrganizationFilter } from '@hotjar/webapp/hooks/useOrganizationFilter';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { Icon, tokens } from '@hotjar/hotsauce';
import { useState } from 'react';
import { MultiSkuSiteBox } from '../MultiSkuSiteBox/MultiSkuSiteBox';
import { SiteBox } from '../SiteBox/SiteBox';

export interface OrganizationsListItem extends Organization {
  sites: SiteWithPriceInfo[];
}

export interface Props {
  organizations: Array<OrganizationsListItem>;
  onPlanChange: (
    organizationId: number,
    siteId: number,
    plan: Pick<Plan, 'identifier'>,
  ) => Promise<void>;
  onCancelDowngrade: (site: SiteDTO) => void;
  updatedSitePriceInfo: Record<number, PriceInfo> | null;
}

const OrgBox = styled.div`
  margin-top: ${tokens.spacer.x5};
  text-align: left;
  border-radius: ${tokens.radius.m};
  border: 1px solid ${tokens.color.ui_border_light};
  ${tokens.elevation(1)};
  display: flex;
  flex-direction: column;
`;

const OrgHeader = styled.div`
  display: flex;
`;

const CostHeader = styled.div`
  flex: 1 1 35%;
  background: rgba(0, 0, 0, 0.025);
  border-left: 1px solid rgba(0, 0, 0, 0.05);
`;

const Details = styled.div`
  flex: 1 1 65%;
`;

const OrgName = styled.div`
  padding: ${tokens.spacer.x2} ${tokens.spacer.x3};
  background: rgba(0, 0, 0, 0.025);
  border-radius: 0 0 3px 3px;
  margin-left: ${tokens.spacer.x4};
  display: inline-block;
  max-width: 400px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconMarginRight = styled(Icon)`
  margin-right: ${tokens.spacer.x2};
`;

export const OrganizationList = ({
  organizations,
  onPlanChange,
  onCancelDowngrade,
  updatedSitePriceInfo,
}: Props): JSX.Element | null => {
  const { t } = useTranslationWithPrefix('billing:organization-list');
  const { data: account } = useOwnedAccount();
  const [filter, setFilter] = useState<string>('');
  const multipleSku = supportsMultipleSku(account);
  const { data: productCatalog } = useProductCatalog();

  const { filteredList: filteredOrgs } = useOrganizationFilter(organizations, filter);

  if (!account) {
    return null;
  }

  return (
    <SearchProvider placeholder={t('filter-placeholder')} onSearchChanged={setFilter}>
      {filteredOrgs.map(({ sites = [], ...organization }) => (
        <OrgBox key={organization.id}>
          <OrgHeader>
            <Details>
              <OrgName>
                <IconMarginRight icon="building_business" /> {organization.name}
              </OrgName>
            </Details>
            <CostHeader />
          </OrgHeader>
          {sites.map((site, index) => (
            <div id={`sampleRateDropdown${index}`} key={site.id}>
              {!multipleSku && (
                <SiteBox
                  account={account}
                  site={site}
                  onPlanChange={onPlanChange}
                  hasDivider={index !== 0}
                  onCancelDowngrade={onCancelDowngrade}
                  updatedSitePriceInfo={updatedSitePriceInfo}
                />
              )}
              {multipleSku && productCatalog && (
                <MultiSkuSiteBox
                  account={account}
                  site={mapSiteDtoToModel(site)}
                  onPlanChange={onPlanChange}
                  hasDivider={index !== 0}
                  productCatalog={productCatalog}
                  priceInfo={
                    updatedSitePriceInfo ? updatedSitePriceInfo?.[site.id] : site.priceInfo
                  }
                />
              )}
            </div>
          ))}
        </OrgBox>
      ))}
    </SearchProvider>
  );
};
